import { readWbsItemSystems, type WbsItemDoc, type WbsItemStatus } from '@mct/shared';
import { classifyDelay } from './wbsTree';

export type DepthMode = 'phase' | 'phaseTask' | 'all';

export interface WbsFilter {
  systems: string[];
  vendors: string[];
  assignees: string[];
  statuses: WbsItemStatus[];
  search: string;
  delayed: boolean;
  /**
   * 完了以外: hide `done` rows. Unlike the positive ステータス chips
   * (which whitelist statuses), this is a single toggle that drops done
   * rows from the direct-match set — but a done parent is still kept in
   * the tree if a non-done descendant pulls it back in via the
   * ancestor-walk, so a section that still has open work stays readable.
   */
  hideDone: boolean;
  depth: DepthMode;
}

export const EMPTY_WBS_FILTER: WbsFilter = {
  systems: [],
  vendors: [],
  assignees: [],
  statuses: [],
  search: '',
  delayed: false,
  hideDone: false,
  depth: 'all',
};

const VALID_STATUSES: readonly WbsItemStatus[] = [
  'not_started',
  'in_progress',
  'done',
  'blocked',
  'cancelled',
];

const VALID_DEPTH: readonly DepthMode[] = ['phase', 'phaseTask', 'all'];

/**
 * Normalize a string for full-text matching: NFKC folds full-width
 * alphanumerics / spaces to their half-width form (so "ＡＰＧ" matches
 * "APG" and 全角 spaces split like half-width ones), then lower-case for
 * a case-insensitive compare.
 */
function normalizeSearch(s: string): string {
  return s.normalize('NFKC').toLowerCase();
}

/**
 * Split a raw query into normalized search tokens. Whitespace-delimited
 * words become an AND set — a row must contain every token to match.
 * NFKC turns the 全角 space (U+3000) into a plain space first, so mixed
 * 半角/全角 spacing is treated the same.
 */
function searchTokensOf(query: string): string[] {
  return normalizeSearch(query).trim().split(/\s+/).filter(Boolean);
}

/**
 * Build the searchable haystack for one item: task name, 詳細, 完了基準,
 * and any linked Backlog issue keys (e.g. "APG-5642"), all normalized.
 */
function searchHaystackOf(i: WbsItemDoc): string {
  return normalizeSearch(
    [
      i.name,
      i.description,
      i.completionCriteria ?? '',
      i.backlogIssueKeys.join(' '),
    ].join('\n'),
  );
}

export function encodeWbsFilter(
  filter: WbsFilter,
  base?: URLSearchParams,
): URLSearchParams {
  const out = new URLSearchParams(base);
  for (const k of [
    'sys',
    'vendor',
    'assignee',
    'status',
    'q',
    'delayed',
    'hideDone',
    'depth',
  ]) {
    out.delete(k);
  }
  if (filter.systems.length) out.set('sys', filter.systems.join(','));
  if (filter.vendors.length) out.set('vendor', filter.vendors.join(','));
  if (filter.assignees.length) out.set('assignee', filter.assignees.join(','));
  if (filter.statuses.length) out.set('status', filter.statuses.join(','));
  if (filter.search) out.set('q', filter.search);
  if (filter.delayed) out.set('delayed', '1');
  if (filter.hideDone) out.set('hideDone', '1');
  if (filter.depth !== 'all') out.set('depth', filter.depth);
  return out;
}

export function decodeWbsFilter(params: URLSearchParams): WbsFilter {
  const statuses = (params.get('status')?.split(',') ?? [])
    .filter(Boolean)
    .filter((s): s is WbsItemStatus =>
      (VALID_STATUSES as readonly string[]).includes(s),
    );
  const depthRaw = params.get('depth');
  const depth: DepthMode = (VALID_DEPTH as readonly string[]).includes(
    depthRaw ?? '',
  )
    ? (depthRaw as DepthMode)
    : 'all';
  return {
    systems: params.get('sys')?.split(',').filter(Boolean) ?? [],
    vendors: params.get('vendor')?.split(',').filter(Boolean) ?? [],
    assignees: params.get('assignee')?.split(',').filter(Boolean) ?? [],
    statuses,
    search: params.get('q') ?? '',
    delayed: params.get('delayed') === '1',
    hideDone: params.get('hideDone') === '1',
    depth,
  };
}

/**
 * Filters a flat item list. Parent rows whose descendants match are kept so
 * the tree stays renderable. `matchIds` returns the set of itemIds that
 * directly matched the filter (used by the UI to dim parents).
 */
export function filterItems(
  items: readonly WbsItemDoc[],
  filter: WbsFilter,
  now: Date,
): { visibleIds: Set<string>; directMatchIds: Set<string> } {
  const byId = new Map(items.map((i) => [i.itemId, i]));
  const parentOf = (id: string): string | null =>
    byId.get(id)?.parentItemId ?? null;

  // parent itemId → direct children (used by the shadow-completion check
  // below; built once so the filter loop stays O(n)).
  const childrenByParent = new Map<string, WbsItemDoc[]>();
  for (const i of items) {
    if (!i.parentItemId) continue;
    let arr = childrenByParent.get(i.parentItemId);
    if (!arr) {
      arr = [];
      childrenByParent.set(i.parentItemId, arr);
    }
    arr.push(i);
  }
  const directChildrenOf = (id: string): readonly WbsItemDoc[] =>
    childrenByParent.get(id) ?? [];

  const searchTokens = searchTokensOf(filter.search);
  const directMatchIds = new Set<string>();
  for (const i of items) {
    const isPhase = i.kind === 'phase';
    const isMilestone = i.kind === 'milestone';
    // Slicer filters (領域 / ベンダー / 担当 / ステータス / 遅延中) treat
    // phases AND milestones as project structure, not data: they're
    // always visible regardless of these settings so the user keeps
    // the section skeleton + key date markers while narrowing the
    // task rows. Phase rows on this project typically carry
    // system="全体" / no vendor / no assignee; milestones likewise
    // have minimal slicer-relevant fields by design.
    if (!isPhase && !isMilestone) {
      // 領域 is multi-value: keep the row if it has ANY selected area (OR).
      const itemSystems = readWbsItemSystems(i);
      if (
        filter.systems.length &&
        !itemSystems.some((s) => filter.systems.includes(s))
      )
        continue;
      if (filter.vendors.length && !filter.vendors.includes(i.vendor)) continue;
      // Filter on the canonical `assigneeId` link to the project's
      // assignee list — picking a chip means "show rows linked to this
      // canonical person".
      if (
        filter.assignees.length &&
        (!i.assigneeId || !filter.assignees.includes(i.assigneeId))
      )
        continue;
      if (filter.statuses.length && !filter.statuses.includes(i.status)) continue;
      if (filter.delayed && !classifyDelay(i, now)) continue;
    }
    // 完了以外: unlike the slicer filters above, this applies to phases
    // and milestones too — a fully-done section should disappear, not
    // linger as an empty skeleton. A done parent only survives via the
    // ancestor-walk below, when a non-done descendant pulls it back in.
    //
    // Shadows are an extra wrinkle: they mirror an original whose status
    // may be 'in_progress' (because the original has other live work in
    // its REAL phase), but inside THIS phase the shadow only matters as
    // a container for its imported children. If every child here is
    // done, the shadow is effectively done for this phase and should
    // disappear, even though `status` says otherwise.
    if (filter.hideDone) {
      if (i.status === 'done') continue;
      if ((i as any).shadowOf != null) {
        // `directChildrenOf` is computed once below (lazily) and
        // closed over here.
        const kids = directChildrenOf(i.itemId);
        if (kids.length > 0 && kids.every((c) => c.status === 'done')) continue;
      }
    }
    // Search applies uniformly: when the user types a keyword they
    // expect to find specific rows, not all phases plus matching tasks.
    // Multiple whitespace-separated words are ANDed across the row's
    // name / 詳細 / 完了基準 / Backlog キー haystack.
    if (searchTokens.length) {
      const hay = searchHaystackOf(i);
      if (!searchTokens.every((t) => hay.includes(t))) continue;
    }
    if (filter.depth === 'phase' && !isPhase) continue;
    if (filter.depth === 'phaseTask') {
      if (!isPhase) {
        const parent = i.parentItemId ? byId.get(i.parentItemId) : null;
        if (!parent || parent.kind !== 'phase') continue;
      }
    }
    directMatchIds.add(i.itemId);
  }

  const visibleIds = new Set<string>(directMatchIds);
  for (const id of directMatchIds) {
    let p = parentOf(id);
    while (p && !visibleIds.has(p)) {
      visibleIds.add(p);
      p = parentOf(p);
    }
  }
  return { visibleIds, directMatchIds };
}

/**
 * Count how many filter constraints are currently applied. Each selected
 * chip (system / vendor / assignee / status) counts as one; search, delayed,
 * and a non-default depth each count as one.
 */
export function countAppliedFilters(filter: WbsFilter): number {
  let n = 0;
  n += filter.systems.length;
  n += filter.vendors.length;
  n += filter.assignees.length;
  n += filter.statuses.length;
  if (filter.search.trim() !== '') n += 1;
  if (filter.delayed) n += 1;
  if (filter.hideDone) n += 1;
  if (filter.depth !== 'all') n += 1;
  return n;
}

export function collectFacets(
  items: readonly WbsItemDoc[],
): {
  systems: string[];
  vendors: string[];
} {
  const sys = new Set<string>();
  const ven = new Set<string>();
  for (const i of items) {
    for (const s of readWbsItemSystems(i)) sys.add(s);
    if (i.vendor) ven.add(i.vendor);
  }
  const sort = (s: Set<string>) =>
    [...s].sort((a, b) => a.localeCompare(b, 'ja'));
  return {
    systems: sort(sys),
    vendors: sort(ven),
  };
}
